import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import {
  fetchInvoices,
  generateInvoiceProposalsNow,
  approveInvoiceProposal,
  rejectInvoiceProposal,
  createManualInvoice,
  updateManualInvoice,
  fetchAssignedPlots,
  fetchBillingCategories,
  cancelInvoice,
  fetchInvoiceHistory,
  fetchInvoicePdfUrl,
  fetchPaymentReceiptPdfUrl,
  viewDraftInvoicePdf,
  fetchZohoItems,
  fetchZohoTaxes,
} from "../api/api";

const STATUS_LABELS = {
  draft: "Draft",
  proposed: "Sent",
  paid: "Paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  draft: "#b45309",
  proposed: "#1d4ed8",
  paid: "#2e7d32",
  overdue: "#dc2626",
  cancelled: "#666",
};

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function currentFyLabel() {
  const today = new Date();
  const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
}

const EMPTY_LINE_ITEM = { name: "", description: "", quantity: 1, rate: "" };

const EMPTY_FORM = {
  plot_ids: [],
  category_id: "",
  fy_label: currentFyLabel(),
  due_date: "",
  discount_percent: 0,
  tax_id: "",
  adjustment: 0,
  order_number: "",
  subject: "",
  customer_notes: "",
  terms_conditions: "",
  line_items: [{ ...EMPTY_LINE_ITEM }],
};

function Invoice() {

  const [invoices, setInvoices] = useState([]);
  const [plots, setPlots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [generating, setGenerating] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [actioningType, setActioningType] = useState(null); // "send" | "cancel"
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [plotSearch, setPlotSearch] = useState("");
  const [contactRequestedOnly, setContactRequestedOnly] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [historyByInvoiceId, setHistoryByInvoiceId] = useState({});
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [draftPdfLoadingId, setDraftPdfLoadingId] = useState(null);
  const [receiptLoadingId, setReceiptLoadingId] = useState(null);
  const [zohoItems, setZohoItems] = useState([]);
  const [zohoTaxes, setZohoTaxes] = useState([]);

  const loadInvoices = async () => {
    setLoading(true);
    setError("");
    try {
      const [data, plotData, categoryData] = await Promise.all([
        fetchInvoices(),
        fetchAssignedPlots(),
        fetchBillingCategories(),
      ]);
      // Best-effort — these pickers are a convenience, not required to use
      // the form, so a failure here shouldn't block loading the page.
      fetchZohoItems().then(setZohoItems).catch(() => {});
      fetchZohoTaxes().then(setZohoTaxes).catch(() => {});
      setInvoices(data);
      setPlots(plotData);
      setCategories(categoryData);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const proposedCount = invoices.filter((inv) => inv.status === "draft").length;
  const contactRequestedCount = invoices.filter((inv) => inv.cancel_requested_at && inv.status === "overdue").length;

  const filteredInvoices = invoices.filter((inv) => {
    if (statusFilter && inv.status !== statusFilter) return false;
    if (contactRequestedOnly && !inv.cancel_requested_at) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      const haystack = `${inv.invoice_number} ${inv.plot_number || ""} ${inv.owner_name || ""}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const togglePlot = (plotId) => {
    setForm((prev) => {
      const plot_ids = prev.plot_ids.includes(plotId)
        ? prev.plot_ids.filter((id) => id !== plotId)
        : [...prev.plot_ids, plotId];
      return { ...prev, plot_ids };
    });
  };

  const handleLineItemChange = (index, field, value) => {
    setForm((prev) => {
      const line_items = prev.line_items.slice();
      line_items[index] = { ...line_items[index], [field]: value };
      return { ...prev, line_items };
    });
  };

  // Picking a Zoho Item auto-fills its saved description/rate onto the
  // row, same as selecting one directly in Zoho Books' own New Invoice
  // screen — the fields stay freely editable afterwards.
  const handlePickZohoItem = (index, zohoItemId) => {
    const item = zohoItems.find((it) => it.zoho_item_id === zohoItemId);
    if (!item) return;
    setForm((prev) => {
      const line_items = prev.line_items.slice();
      line_items[index] = {
        ...line_items[index],
        name: item.name,
        description: item.description || item.name,
        rate: item.rate || line_items[index].rate,
      };
      return { ...prev, line_items };
    });
  };

  const addLineItem = () => {
    setForm((prev) => ({ ...prev, line_items: [...prev.line_items, { ...EMPTY_LINE_ITEM }] }));
  };

  const removeLineItem = (index) => {
    setForm((prev) => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== index) }));
  };

  const lineItemsTotal = form.line_items.reduce(
    (sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.rate) || 0), 0
  );
  // Live preview of the same formula the backend computes on save
  // (_compute_totals in invoice_proposals.py) — updates as any of these
  // fields change. Tax is always an addition here, never a TDS-style
  // withholding — see zoho_books.list_taxes()'s docstring for why.
  const selectedTaxPercentage = zohoTaxes.find((t) => t.zoho_tax_id === form.tax_id)?.percentage || 0;
  const discountAmount = lineItemsTotal * ((Number(form.discount_percent) || 0) / 100);
  const taxableAmount = lineItemsTotal - discountAmount;
  const taxAmount = taxableAmount * (selectedTaxPercentage / 100);
  const liveTotal = taxableAmount + taxAmount + (Number(form.adjustment) || 0);

  const plotSearchQuery = plotSearch.trim().toLowerCase();
  const filteredPlots = plotSearchQuery
    ? plots.filter((p) => `${p.plot_number} ${p.owner_name || ""}`.toLowerCase().includes(plotSearchQuery))
    : plots;

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setError("");
    if (form.plot_ids.length === 0) {
      setError("Select at least one plot.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        plot_ids: form.plot_ids,
        category_id: form.category_id ? Number(form.category_id) : null,
        fy_label: form.fy_label,
        due_date: form.due_date,
        discount_percent: Number(form.discount_percent) || 0,
        tax_id: form.tax_id || null,
        adjustment: Number(form.adjustment) || 0,
        order_number: form.order_number || null,
        subject: form.subject || null,
        customer_notes: form.customer_notes || null,
        terms_conditions: form.terms_conditions || null,
        line_items: form.line_items
          .filter((li) => li.description && li.rate !== "")
          .map((li) => ({ name: li.name || null, description: li.description, quantity: Number(li.quantity) || 1, rate: Number(li.rate) })),
      };
      if (editingId) {
        await updateManualInvoice(editingId, payload);
      } else {
        await createManualInvoice(payload);
      }
      setForm(EMPTY_FORM);
      setPlotSearch("");
      setShowForm(false);
      setEditingId(null);
      await loadInvoices();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (invoice) => {
    setEditingId(invoice.id);
    setForm({
      plot_ids: [invoice.plot_id, ...(invoice.additional_plot_ids || [])],
      category_id: invoice.category_id || "",
      fy_label: invoice.fy_label,
      due_date: invoice.due_date,
      discount_percent: invoice.discount_percent || 0,
      tax_id: invoice.tax_id || "",
      adjustment: invoice.adjustment || 0,
      order_number: invoice.order_number || "",
      subject: invoice.subject || "",
      customer_notes: invoice.customer_notes || "",
      terms_conditions: invoice.terms_conditions || "",
      line_items: invoice.line_items.length > 0
        ? invoice.line_items.map((li) => ({ name: li.name || "", description: li.description, quantity: li.quantity ?? 1, rate: li.rate ?? li.amount }))
        : [{ ...EMPTY_LINE_ITEM }],
    });
    setPlotSearch("");
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setForm(EMPTY_FORM);
    setPlotSearch("");
    setShowForm(false);
    setEditingId(null);
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    setError("");
    try {
      await generateInvoiceProposalsNow();
      await loadInvoices();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (invoiceId) => {
    setActioningId(invoiceId);
    setActioningType("send");
    setError("");
    try {
      await approveInvoiceProposal(invoiceId);
      await loadInvoices();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setActioningId(null);
      setActioningType(null);
    }
  };

  const handleReject = async (invoiceId) => {
    setActioningId(invoiceId);
    setActioningType("cancel");
    setError("");
    try {
      await rejectInvoiceProposal(invoiceId);
      await loadInvoices();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setActioningId(null);
      setActioningType(null);
    }
  };

  const handleCancelInvoice = async (invoice) => {
    const reason = window.prompt(
      `Reason for cancelling ${invoice.invoice_number}? (required — visible in this invoice's history)`
    );
    if (reason === null) return; // dismissed
    if (!reason.trim()) {
      setError("A reason is required to cancel an invoice.");
      return;
    }
    setCancellingId(invoice.id);
    setError("");
    try {
      await cancelInvoice(invoice.id, reason.trim());
      setHistoryByInvoiceId((prev) => {
        const next = { ...prev };
        delete next[invoice.id]; // force a re-fetch next time history is opened
        return next;
      });
      await loadInvoices();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setCancellingId(null);
    }
  };

  const handleViewDraft = async (invoiceId) => {
    setDraftPdfLoadingId(invoiceId);
    setError("");
    try {
      await viewDraftInvoicePdf(invoiceId);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDraftPdfLoadingId(null);
    }
  };

  const handleViewPdf = async (invoiceId) => {
    setPdfLoadingId(invoiceId);
    setError("");
    try {
      const url = await fetchInvoicePdfUrl(invoiceId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleViewReceipt = async (invoice) => {
    setReceiptLoadingId(invoice.id);
    setError("");
    try {
      const url = await fetchPaymentReceiptPdfUrl(invoice.latest_payment_id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setReceiptLoadingId(null);
    }
  };

  const handleToggleHistory = async (invoiceId) => {
    if (expandedId === invoiceId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(invoiceId);
    if (historyByInvoiceId[invoiceId]) return;
    setHistoryLoadingId(invoiceId);
    try {
      const data = await fetchInvoiceHistory(invoiceId);
      setHistoryByInvoiceId((prev) => ({ ...prev, [invoiceId]: data }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setHistoryLoadingId(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>Invoices</h1>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className="register-btn"
              style={{ width: "auto", padding: "10px 20px" }}
              onClick={() => (showForm ? handleCancelForm() : setShowForm(true))}
            >
              {showForm ? "Cancel" : "+ New Invoice"}
            </button>
            <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={handleGenerateNow} disabled={generating}>
              {generating ? "Generating…" : "Generate This Month's Invoices Now"}
            </button>
          </div>
        </div>

        {proposedCount > 0 && (
          <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ background: "#fef3c7", color: "#b45309", padding: "4px 10px", borderRadius: "999px", fontWeight: 600 }}>
              {proposedCount} invoice(s) awaiting approval — see the Draft rows below
            </span>
          </p>
        )}

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        {showForm && (
          <form onSubmit={handleCreateInvoice} style={{ background: "white", borderRadius: "10px", padding: "20px", marginBottom: "20px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>

            <div style={{ marginBottom: "12px" }}>
              <label>Plot(s) — select one or more; the same line items are applied to each</label>
              <input
                type="text"
                placeholder="Search by plot number or customer name"
                className="search-bar"
                value={plotSearch}
                onChange={(e) => setPlotSearch(e.target.value)}
                style={{ marginBottom: "6px" }}
              />
              <div style={{ maxHeight: "160px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "8px", padding: "8px" }}>
                {filteredPlots.length === 0 && <p style={{ color: "#888", margin: 0 }}>No plots match "{plotSearch.trim()}".</p>}
                {filteredPlots.map((p) => (
                  <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0", fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={form.plot_ids.includes(p.id)}
                      onChange={() => togglePlot(p.id)}
                    />
                    {p.plot_number} — {p.owner_name || "Unassigned"}
                  </label>
                ))}
              </div>
              {form.plot_ids.length > 0 && (
                <p style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                  {form.plot_ids.length} plot(s) selected — one invoice will be created covering all of them (the amount below is the total for the invoice, not per plot).
                </p>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
              <div>
                <label>Category</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">Select a category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Financial Year</label>
                <input type="text" required value={form.fy_label} onChange={(e) => setForm({ ...form, fy_label: e.target.value })} />
              </div>
              <div>
                <label>Due Date</label>
                <input type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <label>Order Number</label>
                <input type="text" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} />
              </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label>Subject</label>
              <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Let the customer know what this is for" />
            </div>

            <h4>Line Items</h4>
            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr auto", gap: "8px", marginBottom: "4px" }}>
              <label style={{ fontSize: "12px", color: "#666" }}>Item Name &amp; Description</label>
              <label style={{ fontSize: "12px", color: "#666" }}>Qty</label>
              <label style={{ fontSize: "12px", color: "#666" }}>Rate</label>
              <span />
            </div>
            {form.line_items.map((li, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr auto", gap: "8px", marginBottom: "8px" }}>
                <div>
                  {zohoItems.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => handlePickZohoItem(i, e.target.value)}
                      style={{ marginBottom: "4px", fontSize: "12px", color: "#666" }}
                    >
                      <option value="">Pick a saved item… (optional)</option>
                      {zohoItems.map((item) => (
                        <option key={item.zoho_item_id} value={item.zoho_item_id}>{item.name}</option>
                      ))}
                    </select>
                  )}
                  <input
                    type="text"
                    placeholder="Item Name"
                    value={li.name}
                    onChange={(e) => handleLineItemChange(i, "name", e.target.value)}
                    style={{ fontWeight: 700, marginBottom: "4px" }}
                  />
                  <input type="text" placeholder="Description" required value={li.description} onChange={(e) => handleLineItemChange(i, "description", e.target.value)} />
                </div>
                <input type="number" required value={li.quantity} onChange={(e) => handleLineItemChange(i, "quantity", e.target.value)} />
                <input type="number" placeholder="Rate" required value={li.rate} onChange={(e) => handleLineItemChange(i, "rate", e.target.value)} />
                <button type="button" className="register-btn" style={{ width: "auto", padding: "6px 10px" }} onClick={() => removeLineItem(i)} disabled={form.line_items.length === 1}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="register-btn" style={{ width: "auto", padding: "6px 12px", marginBottom: "12px" }} onClick={addLineItem}>
              + Add Line
            </button>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
              <div>
                <label>Discount %</label>
                <input type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} />
              </div>
              <div>
                <label>Select a Tax</label>
                <select value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}>
                  <option value="">No tax</option>
                  {zohoTaxes.map((t) => (
                    <option key={t.zoho_tax_id} value={t.zoho_tax_id}>{t.name} [{t.percentage}%]</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Adjustment</label>
                <input type="number" value={form.adjustment} onChange={(e) => setForm({ ...form, adjustment: e.target.value })} placeholder="+/- e.g. rounding" />
              </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label>Customer Notes</label>
              <textarea rows={2} value={form.customer_notes} onChange={(e) => setForm({ ...form, customer_notes: e.target.value })} />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label>Terms &amp; Conditions</label>
              <textarea rows={2} value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} />
            </div>

            <div style={{ background: "#f9f9f9", borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Sub Total</span><span>{formatCurrency(lineItemsTotal)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Discount ({Number(form.discount_percent) || 0}%)</span><span>- {formatCurrency(discountAmount)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Tax {selectedTaxPercentage ? `(${selectedTaxPercentage}%)` : ""}</span><span>{formatCurrency(taxAmount)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Adjustment</span><span>{formatCurrency(Number(form.adjustment) || 0)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "8px 0 0", paddingTop: "8px", borderTop: "1px solid #ddd" }}>
                <strong>Total</strong><strong>{formatCurrency(liveTotal)}</strong>
              </p>
            </div>

            <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} disabled={submitting}>
              {submitting ? "Saving…" : editingId ? "Save Changes" : "Create Invoice"}
            </button>
          </form>
        )}

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>

          <input
            type="text"
            placeholder="Search invoice #, plot, or customer"
            className="search-bar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: "220px" }}
          />

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 400, whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={contactRequestedOnly}
              onChange={(e) => setContactRequestedOnly(e.target.checked)}
            />
            Contact Requested only {contactRequestedCount > 0 && `(${contactRequestedCount})`}
          </label>

        </div>

        {loading && <p>Loading invoices…</p>}

        {!loading && filteredInvoices.length === 0 && <p>No invoices match these filters.</p>}

        {!loading && filteredInvoices.length > 0 && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>
            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Invoice #</th>
                  <th>Plot</th>
                  <th>Customer</th>
                  <th>Category</th>
                  <th>FY</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Zoho Books ID</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((inv) => (
                  <React.Fragment key={inv.id}>
                  <tr>
                    <td>{inv.invoice_number}</td>
                    <td>{(inv.all_plot_numbers && inv.all_plot_numbers.length > 0) ? inv.all_plot_numbers.join(", ") : (inv.plot_number || "—")}</td>
                    <td>{inv.owner_name || "—"}</td>
                    <td>{inv.category_name || "—"}</td>
                    <td>{inv.fy_label}</td>
                    <td>{formatCurrency(inv.amount)}</td>
                    <td>{new Date(inv.due_date).toLocaleDateString()}</td>
                    <td style={{ color: STATUS_COLORS[inv.status], fontWeight: 600 }}>
                      {STATUS_LABELS[inv.status] || inv.status}
                      {inv.status === "draft" && inv.zoho_sync_error && (
                        <div style={{ color: "red", fontWeight: 400, fontSize: "12px", marginTop: "4px" }}>{inv.zoho_sync_error}</div>
                      )}
                      {inv.status === "overdue" && inv.cancel_requested_at && (
                        <div style={{ color: "#b45309", fontWeight: 600, fontSize: "12px", marginTop: "4px" }}>
                          ⚠ Contact Requested
                        </div>
                      )}
                      {inv.latest_receipt_number && (
                        <div style={{ color: "#666", fontWeight: 400, fontSize: "12px", marginTop: "4px" }}>
                          Zoho Receipt: {inv.latest_receipt_number}
                        </div>
                      )}
                    </td>
                    <td>{inv.zoho_invoice_id || "—"}</td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {inv.status === "draft" && (
                          <>
                            <button
                              className="register-btn"
                              style={{ width: "auto", padding: "6px 12px" }}
                              onClick={() => handleViewDraft(inv.id)}
                              disabled={draftPdfLoadingId === inv.id}
                            >
                              {draftPdfLoadingId === inv.id ? "Opening…" : "View"}
                            </button>
                            <button
                              className="register-btn"
                              style={{ width: "auto", padding: "6px 12px" }}
                              onClick={() => handleEditClick(inv)}
                            >
                              Edit
                            </button>
                            <button
                              className="login-btn"
                              style={{ width: "auto", padding: "6px 12px" }}
                              onClick={() => handleApprove(inv.id)}
                              disabled={actioningId === inv.id}
                            >
                              {actioningId === inv.id && actioningType === "send" ? "Sending…" : "Send"}
                            </button>
                            <button
                              className="register-btn"
                              style={{ width: "auto", padding: "6px 12px" }}
                              onClick={() => handleReject(inv.id)}
                              disabled={actioningId === inv.id}
                            >
                              {actioningId === inv.id && actioningType === "cancel" ? "Cancelling…" : "Cancel"}
                            </button>
                          </>
                        )}
                        {inv.status === "overdue" && (
                          <button
                            className="register-btn"
                            style={{ width: "auto", padding: "6px 12px" }}
                            onClick={() => handleCancelInvoice(inv)}
                            disabled={cancellingId === inv.id}
                          >
                            {cancellingId === inv.id ? "Cancelling…" : "Cancel"}
                          </button>
                        )}
                        {inv.pdf_available && (
                          <button
                            className="register-btn"
                            style={{ width: "auto", padding: "6px 12px" }}
                            onClick={() => handleViewPdf(inv.id)}
                            disabled={pdfLoadingId === inv.id}
                          >
                            {pdfLoadingId === inv.id ? "Opening…" : "View Invoice PDF"}
                          </button>
                        )}
                        {inv.payment_receipt_available && (
                          <button
                            className="register-btn"
                            style={{ width: "auto", padding: "6px 12px" }}
                            onClick={() => handleViewReceipt(inv)}
                            disabled={receiptLoadingId === inv.id}
                          >
                            {receiptLoadingId === inv.id ? "Opening…" : "View Payment Receipt"}
                          </button>
                        )}
                        <button
                          className="register-btn"
                          style={{ width: "auto", padding: "6px 12px" }}
                          onClick={() => handleToggleHistory(inv.id)}
                        >
                          {expandedId === inv.id ? "Hide History" : "History"}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === inv.id && (
                    <tr>
                      <td colSpan={10} style={{ background: "#fafafa", padding: "12px 16px" }}>
                        {historyLoadingId === inv.id && <p style={{ margin: 0 }}>Loading history…</p>}
                        {historyLoadingId !== inv.id && (historyByInvoiceId[inv.id] || []).length === 0 && (
                          <p style={{ margin: 0, color: "#888" }}>No status changes recorded yet.</p>
                        )}
                        {historyLoadingId !== inv.id && (historyByInvoiceId[inv.id] || []).map((h) => (
                          <div key={h.id} style={{ padding: "6px 0", borderBottom: "1px solid #eee", fontSize: "13px" }}>
                            <strong>{STATUS_LABELS[h.from_status] || h.from_status || "—"} → {STATUS_LABELS[h.to_status] || h.to_status}</strong>
                            {" "}<span style={{ color: "#888" }}>({h.source}{h.changed_by_name ? ` — ${h.changed_by_name}` : ""})</span>
                            {" "}<span style={{ color: "#888" }}>{new Date(h.created_at).toLocaleString()}</span>
                            {h.reason && <div style={{ color: "#444", marginTop: "2px" }}>{h.reason}</div>}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

    </div>

  );

}

export default Invoice;
